import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getAudioContext, 
  startBackgroundMusic, 
  stopBackgroundMusic, 
  isMusicPlaying,
  setAudioVolumes,
  playTickSound
} from '../utils/soundEffects';
import { getSettings, saveSettings } from '../utils/db';

const { 
  FiVolume2, FiVolumeX, FiVolume1, FiMusic, FiPlay, FiPause, 
  FiSliders, FiX, FiCheck, FiSettings, FiHeadphones 
} = FiIcons;

const MUSIC_THEME_OPTIONS = [
  { id: 'arcade_casino', name: 'Arcade & Casino', icon: '🎰', desc: 'Upbeat funk groove' },
  { id: 'carnival_festive', name: 'Festive Carnival', icon: '🎪', desc: 'Bouncy celebration 3/4' },
  { id: 'electronic_rush', name: 'Electronic Synthwave', icon: '⚡', desc: 'High energy arcade' },
  { id: 'ambient_chill', name: 'Ambient Lo-Fi Chill', icon: '🌌', desc: 'Relaxed background beats' },
];

const AudioWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [settings, setSettings] = useState(getSettings());
  const [musicVolume, setMusicVolume] = useState(settings?.musicVolume ?? 0.35);
  const [sfxVolume, setSfxVolume] = useState(settings?.soundVolume ?? 0.7);
  const [musicTheme, setMusicTheme] = useState(settings?.musicTheme || 'arcade_casino');
  const [hasInteracted, setHasInteracted] = useState(false);
  const panelRef = useRef(null);

  // Sync settings whenever updated elsewhere
  useEffect(() => {
    const handleUpdate = () => {
      const current = getSettings();
      setSettings(current);
      setMusicVolume(current?.musicVolume ?? 0.35);
      setSfxVolume(current?.soundVolume ?? 0.7);
      if (current?.musicTheme) setMusicTheme(current.musicTheme);
    };
    window.addEventListener('settingsUpdated', handleUpdate);
    return () => window.removeEventListener('settingsUpdated', handleUpdate);
  }, []);

  // Unlock audio on first user click anywhere
  useEffect(() => {
    const unlockAudio = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        const ctx = getAudioContext();
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        if (settings?.musicEnabled && settings?.musicTheme !== 'none' && !isPlaying) {
          startBackgroundMusic(
            settings.musicTheme || 'arcade_casino', 
            settings.musicVolume ?? 0.35, 
            settings.musicCustomUrl
          );
          setIsPlaying(true);
        }
      }
    };

    window.addEventListener('click', unlockAudio, { once: true });
    window.addEventListener('touchstart', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, [settings?.musicEnabled, settings?.musicTheme, settings?.musicVolume, settings?.musicCustomUrl, hasInteracted, isPlaying]);

  // Click outside to close panel
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle Master Play / Pause Toggle
  const handleTogglePlay = (e) => {
    if (e) e.stopPropagation();
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    if (isPlaying) {
      stopBackgroundMusic();
      setIsPlaying(false);
      const updated = { ...settings, musicEnabled: false };
      setSettings(updated);
      saveSettings(updated);
    } else {
      const themeToPlay = musicTheme === 'none' ? 'arcade_casino' : musicTheme;
      const vol = musicVolume > 0 ? musicVolume : 0.35;
      if (musicVolume === 0) setMusicVolume(0.35);
      startBackgroundMusic(themeToPlay, vol, settings?.musicCustomUrl);
      setIsPlaying(true);
      const updated = { 
        ...settings, 
        musicEnabled: true, 
        musicTheme: themeToPlay,
        musicVolume: vol 
      };
      setSettings(updated);
      saveSettings(updated);
    }
  };

  // Handle Music Volume Slider Change
  const handleMusicVolumeChange = (newVal) => {
    const vol = parseFloat(newVal);
    setMusicVolume(vol);
    setAudioVolumes({ musicVolume: vol });

    const updated = { ...settings, musicVolume: vol };
    setSettings(updated);
    saveSettings(updated);

    // If sliding up from 0 while paused, automatically resume music
    if (vol > 0 && !isPlaying && settings?.musicEnabled !== false) {
      const themeToPlay = musicTheme === 'none' ? 'arcade_casino' : musicTheme;
      startBackgroundMusic(themeToPlay, vol, settings?.musicCustomUrl);
      setIsPlaying(true);
    } else if (vol === 0 && isPlaying) {
      // If user slides to 0, it's effectively muted
    }
  };

  // Handle SFX Volume Slider Change
  const handleSfxVolumeChange = (newVal) => {
    const vol = parseFloat(newVal);
    setSfxVolume(vol);
    setAudioVolumes({ sfxVolume: vol });
    playTickSound(900, 0.05);

    const updated = { ...settings, soundVolume: vol };
    setSettings(updated);
    saveSettings(updated);
  };

  // Handle Theme Change
  const handleThemeChange = (newTheme) => {
    setMusicTheme(newTheme);
    const updated = { ...settings, musicTheme: newTheme, musicEnabled: true };
    setSettings(updated);
    saveSettings(updated);

    if (isPlaying || settings?.musicEnabled) {
      stopBackgroundMusic();
      startBackgroundMusic(newTheme, musicVolume, settings?.musicCustomUrl);
      setIsPlaying(true);
    }
  };

  const volumePercent = Math.round(musicVolume * 100);
  const sfxPercent = Math.round(sfxVolume * 100);

  return (
    <div ref={panelRef} className="fixed bottom-6 right-24 z-[99]">
      {/* Floating Pill Trigger Button */}
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`h-14 px-4 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)] border backdrop-blur-md flex items-center gap-2.5 text-xs font-bold transition-all cursor-pointer ${
          isPlaying && musicVolume > 0
            ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40 shadow-amber-500/10'
            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
        }`}
        title="Background Music & Audio Volume Adjustments"
        aria-label="Audio Controls"
      >
        <SafeIcon 
          icon={isPlaying && musicVolume > 0 ? FiVolume2 : (musicVolume === 0 ? FiVolumeX : FiMusic)} 
          className="text-lg flex-shrink-0" 
        />

        {/* Text Status & Volume percentage */}
        <div className="flex flex-col text-left leading-none pr-1">
          <span className="text-[10px] font-black uppercase tracking-wider opacity-75">
            {isPlaying && musicVolume > 0 ? 'BGM ON' : 'BGM OFF'}
          </span>
          <span className="text-xs font-black font-mono mt-0.5">
            {isPlaying && musicVolume > 0 ? `${volumePercent}%` : 'Muted'}
          </span>
        </div>

        {/* Animated Soundwave Equalizer Bars */}
        {isPlaying && musicVolume > 0 && (
          <div className="flex items-end gap-0.5 h-3.5 pl-0.5">
            <span className="w-0.5 bg-amber-500 dark:bg-amber-400 rounded-full animate-bounce h-2.5" style={{ animationDelay: '0ms' }} />
            <span className="w-0.5 bg-amber-500 dark:bg-amber-400 rounded-full animate-bounce h-3.5" style={{ animationDelay: '150ms' }} />
            <span className="w-0.5 bg-amber-500 dark:bg-amber-400 rounded-full animate-bounce h-2" style={{ animationDelay: '300ms' }} />
          </div>
        )}
      </motion.button>

      {/* Expandable Audio Control Panel Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 15 }}
            transition={{ duration: 0.16 }}
            className="absolute bottom-16 right-0 w-80 sm:w-96 p-5 rounded-2xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-700/80 shadow-2xl text-slate-800 dark:text-slate-100 space-y-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
                  <SafeIcon icon={FiHeadphones} className="text-base" />
                </div>
                <div>
                  <h4 className="text-sm font-black tracking-tight leading-none">Audio & Music Center</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Adjust background music volume live</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                aria-label="Close"
              >
                <SafeIcon icon={FiX} className="text-base" />
              </button>
            </div>

            {/* Master Music Play / Pause Action Button */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center gap-2.5">
                <SafeIcon icon={FiMusic} className={`text-lg ${isPlaying ? 'text-amber-500' : 'text-slate-400'}`} />
                <div>
                  <div className="text-xs font-bold leading-none">Background Music</div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {isPlaying ? 'Currently streaming' : 'Music playback is paused'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleTogglePlay}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer shadow-sm ${
                  isPlaying
                    ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/20'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20'
                }`}
              >
                <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-xs" />
                <span>{isPlaying ? 'Pause' : 'Play Music'}</span>
              </button>
            </div>

            {/* Background Music Volume Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                  <SafeIcon icon={FiVolume2} className="text-amber-500" />
                  <span>Music Volume</span>
                </span>
                <span className="px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 font-mono font-black text-xs">
                  {volumePercent}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={musicVolume}
                onChange={(e) => handleMusicVolumeChange(e.target.value)}
                className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
              />

              {/* Volume Preset Quick Buttons */}
              <div className="flex items-center justify-between gap-1 pt-1">
                {[
                  { label: '0%', val: 0 },
                  { label: '25%', val: 0.25 },
                  { label: '50%', val: 0.50 },
                  { label: '75%', val: 0.75 },
                  { label: '100%', val: 1.0 }
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handleMusicVolumeChange(preset.val)}
                    className={`flex-1 py-1 rounded-lg text-[10px] font-black border transition-all cursor-pointer ${
                      Math.abs(musicVolume - preset.val) < 0.05
                        ? 'bg-amber-500 text-slate-950 border-amber-500 shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Music Theme Selector */}
            <div className="space-y-2 pt-1 border-t border-slate-100 dark:border-slate-800/80">
              <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                <span>Music Style / Theme</span>
                <span className="text-[10px] text-slate-400">Live switch</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {MUSIC_THEME_OPTIONS.map((theme) => {
                  const isSelected = musicTheme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => handleThemeChange(theme.id)}
                      className={`p-2 rounded-xl text-left border transition-all cursor-pointer flex items-center gap-2 ${
                        isSelected
                          ? 'bg-amber-500/15 border-amber-500 text-amber-700 dark:text-amber-300 shadow-sm'
                          : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <span className="text-base flex-shrink-0">{theme.icon}</span>
                      <div className="min-w-0">
                        <div className="text-[11px] font-bold truncate leading-tight">{theme.name}</div>
                        <div className="text-[9px] text-slate-400 truncate leading-none mt-0.5">{theme.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sound Effects (SFX) Volume Slider */}
            <div className="space-y-2 pt-1 border-t border-slate-100 dark:border-slate-800/80">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                  <SafeIcon icon={FiSliders} className="text-indigo-500" />
                  <span>Game Sound Effects (SFX)</span>
                </span>
                <span className="px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-mono font-black text-xs">
                  {sfxPercent}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={sfxVolume}
                  onChange={(e) => handleSfxVolumeChange(e.target.value)}
                  className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => playTickSound(1000, 0.06)}
                  className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-[10px] font-black border border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
                  title="Test Sound Effect"
                >
                  Test Beep
                </button>
              </div>
            </div>

            {/* Footer notice */}
            <div className="text-[10px] text-center text-slate-400 dark:text-slate-500 pt-1">
              ✓ Settings auto-saved & synchronized across all game modes & displays.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AudioWidget;
